import React from "react";
import { Check, Github, X } from "lucide-react";
import GithubIntegrationAnimation from "./GithubIntegrationAnimation";
import { Zap } from "@/components/Icons.tsx";

interface GithubInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  githubInstallationId?: string;
}

export const GithubInstallModal: React.FC<GithubInstallModalProps> = ({
  isOpen,
  onClose,
  orgId,
  githubInstallationId,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        className="w-full max-w-md bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900 rounded-t-xl">
          <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
            <Zap size={20} />
            <h3 className="font-bold text-slate-900 dark:text-slate-100">
              Automation
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300 transition-colors p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <GithubIntegrationAnimation />
          <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
            {githubInstallationId
              ? "Connection Active"
              : "Connect GitHub Actions"}
          </h4>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
            {githubInstallationId
              ? "Your GitHub organisation is connected. You can now run automated workflows directly from your plans."
              : "Bring your runbooks to life. By connecting GitHub, you can transform manual steps into automated Actions that run with a single click."}
          </p>

          {!githubInstallationId && (
            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                  <Check size={12} strokeWidth={3} />
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium text-nowrap">
                  Trigger workflows from any task
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                  <Check size={12} strokeWidth={3} />
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium text-nowrap">
                  Live status updates & log streaming
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                localStorage.setItem("pending_gh_install_org_id", orgId);
                const url = `https://github.com/apps/taskplan-automation/installations/new?state=${orgId}`;
                window.location.href = url;
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition shadow-md shadow-indigo-500/20"
            >
              <Github size={18} />
              {githubInstallationId
                ? "Configure on GitHub"
                : "Install GitHub App"}
            </button>
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GithubInstallModal;
